import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { BRAND_CONFIG } from "@utils/brand_config_enums";
import { resolveBrand } from "@utils/branding";
import { isTestingMode } from "@utils/isTestingMode";
import {
	IS_ACTIVATION_ROLE,
	IS_ADMIN_ROLE,
	IS_DISTRIBUTOR_ROLE,
	IS_RESELLER_ROLE,
	IS_SALES_STAFF_ROLE,
} from "@utils/user_roles";
import VouchersConfigList from "pages/VoucherConfiguration/VouchersConfigList";
import posthog from "posthog-js";
import { lazy, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import "./App.css";
import ProtectedRoute from "./components/route/ProtectedRoute";
import PublicRoute from "./components/route/PublicRoute";
import { FLAGS } from "./config/growthbook/flags.js";
import AuthLayout from "./layouts/AuthLayout";
import MainLayout from "./layouts/MainLayout";
import FormUsers from "./pages/AccountManagement/Users/FormUser";
import UsersList from "./pages/AccountManagement/Users/UsersList";
import Maintenance from "./pages/Maintenance/Maintenance";
import VesselDetails from "./pages/Vessels/VesselDeatails/VesselDetails";
import Vessels from "./pages/Vessels/Vessels";

// Layouts

// Pages
const Home = lazy(() => import("./pages/Home/Home"));
const Login = lazy(() => import("./pages/Login/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword/RessetPassword"));
const NewPassword = lazy(() => import("./pages/NewPassword/NewPassword"));
const AddCode = lazy(() => import("./pages/AddCode/AddCode"));
const FirstLogin = lazy(() => import("./pages/FirstLogin/FirstLogin"));

const KitDetailsWrapper = lazy(() =>
	import("./pages/Vessels/KitDetails/KitDetails")
		.then((mod) => {
			console.log("KitDetails module loaded:", mod);
			return mod;
		})
		.catch((e) => {
			console.error("KitDetails failed:", e);
			return { default: () => <h1>LOAD ERROR: {e.message}</h1> };
		}),
);
const Profile = lazy(() => import("./pages/Profile/Profile"));
const Stock = lazy(() => import("./pages/Stock/Stock"));
const FormDevice = lazy(() => import("./pages/Stock/FormDevice"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));

// Account Management
const BusinessUnitsList = lazy(
	() => import("./pages/AccountManagement/BusinessUnits/BusinessUnitsList"),
);
const FormBusinessUnit = lazy(
	() => import("./pages/AccountManagement/BusinessUnits/FormBusinessUnit"),
);
const ClientsList = lazy(() => import("./pages/AccountManagement/Clients/ClientsList"));
const FormClient = lazy(() => import("./pages/AccountManagement/Clients/FormClients"));

const FormShowClients = lazy(() => import("./pages/AccountManagement/Clients/FormShowClients"));
const FormShowDistributors = lazy(
	() => import("./pages/AccountManagement/Distributors/FormShowDistributors"),
);

const FormViewScaps = lazy(() => import("./pages/Credit Limit Management/Scaps/FormViewScaps"));
// const FormUsers = lazy(
//   () => import("./pages/AccountManagement/Users/FormUser")
// );
const DistributorPool = lazy(() => import("./pages/DistributorPool/DistributorPool"));
const RolesList = lazy(() => import("./pages/AccountManagement/Roles/RolesList"));
const FormRoles = lazy(() => import("./pages/AccountManagement/Roles/FormRoles"));
const DistributorsList = lazy(
	() => import("./pages/AccountManagement/Distributors/DistributorsList"),
);
const FormDistributors = lazy(
	() => import("./pages/AccountManagement/Distributors/FormDistributors"),
);
const ResellersList = lazy(() => import("./pages/AccountManagement/Resellers/ResellersList"));
const FormResellers = lazy(() => import("./pages/AccountManagement/Resellers/FormResellers"));
const AccountNumbersList = lazy(
	() => import("./pages/AccountManagement/AccountNumbers/AccountNumbersList"),
);
const FormAccountNumbers = lazy(
	() => import("./pages/AccountManagement/AccountNumbers/FormAccountNumbers"),
);
const BusinessUnitClientsActivationSettings = lazy(
	() => import("./pages/AccountManagement/BusinessUnits/BusinessUnitClientsActivationSettings"),
);
const RequestManagementList = lazy(
	() => import("./pages/AccountManagement/Request Management/RequestManagementList"),
);
const RequestManagementDetails = lazy(
	() => import("./pages/AccountManagement/Request Management/RequestDetails"),
);

// Logs
const GeneralActivitiesLogs = lazy(
	() => import("./pages/LogsManagement/GeneralActivitiesLogs/GeneralActivitiesLogs"),
);
const CrudDetailsLogs = lazy(
	() => import("./pages/LogsManagement/GeneralActivitiesLogs/CrudDetailsLogs"),
);
const GeneralCronLogs = lazy(() => import("./pages/LogsManagement/GeneralCron/GeneralCronLogs"));
const CronDetailsLogs = lazy(() => import("./pages/LogsManagement/GeneralCron/CronDetailsLogs"));
const ApiLogs = lazy(() => import("./pages/LogsManagement/ApiLogs/ApiLogs"));
const ApiDetailsLogs = lazy(() => import("./pages/LogsManagement/ApiLogs/ApiDetailsLogs"));
const ActivationLogs = lazy(() => import("./pages/LogsManagement/ActivaionLogs/ActivationLogs"));
const ActivationDetailsLogs = lazy(
	() => import("./pages/LogsManagement/ActivaionLogs/ActivationDetailsLogs"),
);
const DeactivationLogs = lazy(
	() => import("./pages/LogsManagement/DectivaionLogs/DeactivationLogs"),
);
const DeactivationDetails = lazy(
	() => import("./pages/LogsManagement/DectivaionLogs/DeactivationDetails"),
);

// Other modules
const Notifications = lazy(() => import("./pages/Notifications/Notifications"));
const NotificationDetails = lazy(() => import("./pages/Notifications/NotificationDetails"));
const ProjectOverview = lazy(() => import("./pages/ProjectOverview/ProjectOverview"));

const ManagementPool = lazy(() => import("./pages/ManagementPool/ManagementPool"));
const ScapsList = lazy(() => import("./pages/Credit Limit Management/Scaps/ScapsList"));
const FormScaps = lazy(() => import("./pages/Credit Limit Management/Scaps/FormScaps"));
const CreditLimitList = lazy(() => import("./pages/ProvManagement/CreditLimit/CreditLimitList"));
const DevicesList = lazy(() => import("./pages/Centreon/Devices/DevicesList"));
const FormDevices = lazy(() => import("./pages/Centreon/Devices/FormDevices"));
const FormVessel = lazy(() => import("./pages/Vessels/Vessel/FormVessel"));
const Testing = lazy(() => import("./pages/Testing/Testing"));

function normalizeRoute(pathname) {
	return pathname
		.split("/")
		.map((segment) => {
			if (/^\d+$/.test(segment)) {
				return ":id";
			}
			if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) {
				return ":id";
			}
			return segment;
		})
		.join("/");
}

function PageViewTracker() {
	const location = useLocation();
	useEffect(() => {
		const route = normalizeRoute(location.pathname);
		posthog.capture("$pageview", {
			route,
			$pathname: route,
		});
	}, [location.pathname]);
	return null;
}

const LoadingFallback = () => (
	<div className="flex h-screen items-center justify-center bg-black">
		<div className="h-16 w-16 animate-spin rounded-full border-2 border-gray-900 border-t-transparent"></div>
	</div>
);

const LoadingAuthFallback = () => (
	<div className="flex h-screen items-center justify-center bg-transparent">
		<div className="h-16 w-16 animate-spin rounded-full border-2 border-gray-900 border-t-transparent"></div>
	</div>
);
function App() {
	const maintenance = useFeatureIsOn(FLAGS.app.maintenanceMode);

	if (maintenance) {
		return <Maintenance />;
	}

	let buName = useSelector((state) => state.auth.user?.business_units[0]?.name);

	let userRole = useSelector((state) => state.auth.user.role);
	let distributorname = useSelector((state) => state.auth.user?.distributor?.name);

	const RoleBasedElement = ({ component: ComponentIfAdmin, fallback }) => {
		const isAdmin =
			userRole?.id === IS_ADMIN_ROLE ||
			userRole?.parent_id === IS_ADMIN_ROLE ||
			userRole?.id === IS_SALES_STAFF_ROLE ||
			userRole?.id === IS_ACTIVATION_ROLE ||
			(userRole?.parent_id === null &&
				userRole?.id === IS_DISTRIBUTOR_ROLE &&
				(distributorname === "INARIAGEIE" ||
					distributorname === "Distributor Demo" ||
					distributorname === "distributor Hajer"));

		const isAdistaAdmin =
			(userRole?.parent_id === null &&
				userRole?.id === IS_DISTRIBUTOR_ROLE &&
				buName === "ADISTA") ||
			(userRole?.id === IS_RESELLER_ROLE && buName === "ADISTA");
		const isTerrenovaAdmin =
			userRole?.parent_id === null &&
			userRole?.id === IS_DISTRIBUTOR_ROLE &&
			buName === "TERRENOVA";

		if (isAdmin || isAdistaAdmin || isTerrenovaAdmin) {
			return <ComponentIfAdmin />;
		}

		return fallback;
	};

	const brand = buName || resolveBrand();

	useEffect(() => {
		if (brand === "ADISTA") {
			document.title = "Adista";
		} else if (brand === "TERRENOVA") {
			document.title = "Terrenova";
		} else {
			document.title = "Optiview";
		}
	}, [brand]);

	const config = BRAND_CONFIG[brand] || BRAND_CONFIG["OPTIVIEW"];

	return (
		<>
			<Helmet>
				<title>{config?.title}</title>
				<link rel="icon" type="image/svg+xml" href={config?.icon} />
			</Helmet>
			<Router
				future={{
					v7_startTransition: false,
					v7_relativeSplatPath: true,
					v7_fetcherPersist: true,
				}}
			>
				<PageViewTracker />
				<Routes>
					{/* ************* NotFound Page ************ */}

					<Route
						path="*"
						element={
							<Suspense fallback={<LoadingFallback />}>
								<NotFound />
							</Suspense>
						}
					/>

					{/* ************* Login ************ */}

					<Route
						path="/login"
						element={
							<PublicRoute>
								<AuthLayout />
							</PublicRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingAuthFallback />}>
									<Login />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Reset Password ************ */}

					<Route
						path="/reset-password"
						element={
							<PublicRoute>
								<AuthLayout />
							</PublicRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingAuthFallback />}>
									<ResetPassword />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* New Password ************ */}

					<Route
						path="/new-password"
						element={
							<PublicRoute>
								<AuthLayout />
							</PublicRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingAuthFallback />}>
									<NewPassword />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Add Password ************ */}

					<Route
						path="/add-code"
						element={
							<PublicRoute>
								<AuthLayout />
							</PublicRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingAuthFallback />}>
									<AddCode />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* First Login ************ */}

					<Route
						path="/first-login"
						element={
							<PublicRoute>
								<AuthLayout />
							</PublicRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingAuthFallback />}>
									<FirstLogin />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Profile ************ */}

					<Route
						path="/profile"
						element={
							<ProtectedRoute>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<Profile />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Notification Module ************ */}

					<Route
						path="/notifications"
						element={
							<ProtectedRoute>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<Notifications />
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/notifications/:id"
						element={
							<ProtectedRoute>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<NotificationDetails />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Dashboard ************ */}

					<Route
						path="/"
						element={
							<ProtectedRoute>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<Home />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Stock Module ************ */}

					<Route
						path="/stock"
						element={
							<ProtectedRoute resourceName="stock" permission="view stocks">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("stock") ? <Testing /> : <Stock />}
								</Suspense>
							}
						/>
						<Route
							path="add-device"
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("stock") ? <Testing /> : <FormDevice />}
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="centreon"
						element={
							<ProtectedRoute resourceName="centreon" permission="view Centreon">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							path="Centreon Devices"
							element={
								<Suspense fallback={<LoadingFallback />}>
									<DevicesList />
								</Suspense>
							}
						/>
						<Route
							path="Centreon Devices/create"
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormDevices />
								</Suspense>
							}
						/>
						<Route
							path="Centreon Devices/edit/:id"
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormDevices />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Vessel/site Module ************ */}

					<Route
						path="/vessels_site"
						element={
							<ProtectedRoute resourceName="vessels_site" permission="view vessels_site">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("vessels_site") ? <Testing /> : <Vessels />}
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/vessel/:id"
						element={
							<ProtectedRoute resourceName="vessels_site" permission="view vessels_site">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<VesselDetails />
								</Suspense>
							}
						/>
						<Route
							path=":idkit"
							element={
								<Suspense fallback={<LoadingFallback />}>
									<KitDetailsWrapper />
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/vessels_site/create"
						element={
							<ProtectedRoute
								resourceName="vessels_site"
								permission="create vessels_site"
								subpermission="create vessels"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormVessel />
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/vessels_site/edit/:id"
						element={
							<ProtectedRoute resourceName="vessels_site" permission="update vessels_site">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormVessel />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Project Overview Module ************ */}

					<Route
						path="/project_overview"
						element={
							<ProtectedRoute resourceName="project_overview" permission="view project overview">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("project_overview") ? <Testing /> : <ProjectOverview />}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/management_pool"
						element={
							<ProtectedRoute resourceName="management_pool" permission="view management pool">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("project_overview") ? <Testing /> : <ManagementPool />}
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Project Overview Module ************ */}

					<Route
						path="/distributor_pool"
						element={
							<ProtectedRoute resourceName="distributor_pool" permission="view distributor pool">
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("distributor_pool") ? <Testing /> : <DistributorPool />}
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Account Management Module ************ */}

					{/* Users */}
					<Route
						path="account_management/users"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view users"
								subresourceName="account_management_users"
								subpermission="view users"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<RoleBasedElement
										component={UsersList}
										fallback={
											isTestingMode("account_management_users") ? (
												<Testing />
											) : (
												<FormUsersRouteElement />
											)
										}
									/>
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/account_management/users/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view users"
								subresourceName={"account_management_users"}
								subpermission="create users"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<RoleBasedElement
									component={FormUsers}
									fallback={
										isTestingMode("account_management_users") ? (
											<Testing />
										) : (
											<FormUsersRouteElement />
										)
									}
								/>
							}
						/>
					</Route>
					<Route
						path="/account_management/users/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view users"
								subresourceName={"account_management_users"}
								subpermission="update users"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<RoleBasedElement
									component={FormUsers}
									fallback={
										isTestingMode("account_management_users") ? (
											<Testing />
										) : (
											<FormUsersRouteElement />
										)
									}
								/>
							}
						/>
					</Route>

					{/* Roles */}

					<Route
						path="account_management/roles"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view roles"
								subresourceName="account_management_roles"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_roles") ? <Testing /> : <RolesList />}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/roles/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view roles"
								subresourceName="account_management_roles"
								subpermission="create roles"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormRoles />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/roles/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view roles"
								subresourceName="account_management_roles"
								subpermission="update roles"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormRoles />
								</Suspense>
							}
						/>
					</Route>

					{/* Clients */}
					<Route
						path="account_management/clients"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view clients"
								subresourceName="account_management_clients"
								subpermission="view clients"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_clients") ? <Testing /> : <ClientsList />}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/clients/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view clients"
								subresourceName="account_management_clients"
								subpermission="create clients"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormClient />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/clients/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view clients"
								subresourceName="account_management_clients"
								subpermission="update clients"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormClient />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/clients/:id/configurations"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view clients"
								subresourceName="account_management_clients"
								subpermission="view configuration"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<BusinessUnitClientsActivationSettings typeEntity="clients" />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/clients/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view clients"
								subresourceName="account_management_clients"
								subpermission="view client details"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormShowClients />
								</Suspense>
							}
						/>
					</Route>

					{/* Distributors */}

					<Route
						path="account_management/distributors"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view distributors"
								subresourceName="account_management_distributors"
								subpermission="view distributors"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_distributors") ? (
										<Testing />
									) : (
										<DistributorsList />
									)}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/distributors/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view distributors"
								subresourceName="account_management_distributors"
								subpermission="create distributors"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormDistributors />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/distributors/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view distributors"
								subresourceName="account_management_distributors"
								subpermission="update distributors"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormDistributors />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/distributors/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view distributors"
								subresourceName="account_management_distributors"
								subpermission="view distributor details"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormShowDistributors />
								</Suspense>
							}
						/>
					</Route>

					{/* Business Units */}

					<Route
						path="account_management/business Units"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view business units"
								subresourceName="account_management_business_units"
								subpermission="view business units"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_business_units") ? (
										<Testing />
									) : (
										<BusinessUnitsList />
									)}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/business Units/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view business Units"
								subresourceName="account_management_business_units"
								subpermission="create business units"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormBusinessUnit />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/business Units/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view business Units"
								subresourceName="account_management_business_units"
								subpermission="update business units"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormBusinessUnit />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/business Units/:id/configurations"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view business Units"
								subresourceName="account_management_business_units"
								subpermission="update business units"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<BusinessUnitClientsActivationSettings typeEntity="business_unit" />
								</Suspense>
							}
						/>
					</Route>

					{/* Resellers */}

					<Route
						path="account_management/resellers"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view resellers"
								subresourceName="account_management_resellers"
								subpermission="view resellers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_resellers") ? <Testing /> : <ResellersList />}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/resellers/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view resellers"
								subresourceName="account_management_resellers"
								subpermission="create resellers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormResellers />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/resellers/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view resellers"
								subresourceName="account_management_resellers"
								subpermission="update resellers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormResellers />
								</Suspense>
							}
						/>
					</Route>

					{/* Account Numbers */}

					<Route
						path="account_management/Account Numbers"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view account numbers"
								subresourceName="account_management_account_numbers"
								subpermission="view account numbers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									{isTestingMode("account_management_account_numbers") ? (
										<Testing />
									) : (
										<AccountNumbersList />
									)}
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/Account Numbers/create"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view account numbers"
								subresourceName="account_management_account_numbers"
								subpermission="create account numbers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormAccountNumbers />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/Account Numbers/edit/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view account numbers"
								subresourceName="account_management_account_numbers"
								subpermission="update account numbers"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormAccountNumbers />
								</Suspense>
							}
						/>
					</Route>

					{/* Request Mangement */}

					<Route
						path="account_management/Request Mangement"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view request managements"
								subresourceName="account_management_request_management"
								subpermission="view request management"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<RequestManagementList />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/account_management/Request Mangement/details/:id"
						element={
							<ProtectedRoute
								resourceName="account_management"
								permission="view request managements"
								subresourceName="account_management_request_management"
								subpermission="view request management details"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<RequestManagementDetails />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Logs Module ************ */}

					{/* General Activities Logs */}

					<Route
						path="logs/General Activities Logs"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view activity log"
								subresourceName="logs_general_activities"
								subpermission="view activity log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<GeneralActivitiesLogs />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="logs/General Activities Logs/details/:id"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view activity log"
								subresourceName="logs_general_activities"
								subpermission="view activity log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<CrudDetailsLogs />
								</Suspense>
							}
						/>
					</Route>

					{/* Cron */}

					<Route
						path="logs/Cron"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view cron log"
								subresourceName="logs_general_cron"
								subpermission="view cron log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<GeneralCronLogs />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="logs/Cron/details/:id"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view cron log"
								subresourceName="logs_general_cron"
								subpermission="view cron log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<CronDetailsLogs />
								</Suspense>
							}
						/>
					</Route>

					{/* Api */}

					<Route
						path="logs/Api"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view api log"
								subresourceName="logs_general_api"
								subpermission="view api log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<ApiLogs />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="logs/Api/details/:id"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view api log"
								subresourceName="logs_general_api"
								subpermission="view api log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<ApiDetailsLogs />
								</Suspense>
							}
						/>
					</Route>

					{/* Activation */}

					<Route
						path="logs/Activation"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view activation log"
								subresourceName="logs_general_activation"
								subpermission="view activation log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<ActivationLogs />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="logs/Activation/details/:id"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view activation log"
								subresourceName="logs_general_activation"
								subpermission="view activation log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<ActivationDetailsLogs />
								</Suspense>
							}
						/>
					</Route>

					{/* Deactivation */}

					<Route
						path="logs/Deactivation"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view desactivation log"
								subresourceName="logs_general_desactivation"
								subpermission="view desactivation log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<DeactivationLogs />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="logs/Deactivation/details/:id"
						element={
							<ProtectedRoute
								resourceName="logs"
								permission="view desactivation log"
								subresourceName="logs_general_desactivation"
								subpermission="view desactivation log"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<DeactivationDetails />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Prov Management ************ */}

					{/* Credit Limit */}

					<Route
						path="prov_management/Credit Limit"
						element={
							<ProtectedRoute
								resourceName="prov_management"
								permission="view credit limit"
								subresourceName="prov_management_credit_limit"
								subpermission="view credit limit"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<CreditLimitList />
								</Suspense>
							}
						/>
					</Route>

					{/* ************* Credit Limit Management ************ */}

					{/* Scap */}

					<Route
						path="/credit_limit_management/Scaps"
						element={
							<ProtectedRoute
								resourceName="credit_limit_management"
								permission="view scaps"
								subresourceName="scaps"
								subpermission="view scaps"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<ScapsList />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/credit_limit_management/Scaps/create"
						element={
							<ProtectedRoute
								resourceName="credit_limit_management"
								permission="create scaps"
								subresourceName="scaps"
								subpermission="create scaps"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormScaps />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/credit_limit_management/Scaps/edit/:id"
						element={
							<ProtectedRoute
								resourceName="credit_limit_management"
								permission="update scaps"
								subresourceName="scaps"
								subpermission="update scaps"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormScaps />
								</Suspense>
							}
						/>
					</Route>

					<Route
						path="/credit_limit_management/Scaps/:id"
						element={
							<ProtectedRoute
								resourceName="credit_limit_management"
								permission="update scaps"
								subresourceName="scaps"
								subpermission="view details scaps"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<FormViewScaps />
								</Suspense>
							}
						/>
					</Route>
					{/* ************* iec voucher manager Module ************ */}

					<Route
						path="/iec_voucher_manager"
						element={
							<ProtectedRoute
								resourceName="iec_voucher_manager"
								permission="view iec voucher manager"
							>
								<MainLayout />
							</ProtectedRoute>
						}
					>
						<Route
							index
							element={
								<Suspense fallback={<LoadingFallback />}>
									<VouchersConfigList />
								</Suspense>
							}
						/>
					</Route>
				</Routes>
			</Router>
		</>
	);
}

export default App;
